import { formatCompact } from './TrendChart';

export interface BarRow {
  label: string;
  value: number;
}

interface BarListProps {
  rows: BarRow[];
  /** Rows shown before "and N more". Keeps the card scannable. */
  limit?: number;
  emptyLabel?: string;
  /** The value column's text. Default: {@link formatCompact}; money passes its own so it is never abbreviated. */
  formatValue?: (value: number) => string;
}

/**
 * Horizontal magnitude comparison — the right form for "compare low→high" with long
 * category names (countries, URLs, campaign names).
 *
 * ONE hue (sequential job), not categorical: these bars are the same measure sliced by
 * a dimension, so giving each row its own colour would imply an identity that isn't
 * there and would burn through the categorical slots for nothing.
 */
export function BarList({ rows, limit = 8, emptyLabel = 'No data yet.', formatValue = formatCompact }: BarListProps) {
  if (rows.length === 0) {
    return <div className="empty-state t-body">{emptyLabel}</div>;
  }

  const shown = rows.slice(0, limit);
  const max = Math.max(...shown.map((r) => r.value), 1);

  return (
    <>
      <div className="barlist">
        {shown.map((row) => {
          const pct = Math.max(2, (row.value / max) * 100);

          // Where the label goes. Two independent reasons to put it INSIDE the bar:
          //   1. the bar is long enough to hold a short label comfortably, or
          //   2. the bar is so long there is no usable room left of the value column.
          // (2) matters because the outside label's width is capped at the space that
          // actually remains; without it, a long title on a near-full bar would be
          // allotted ~0px and disappear entirely — trading an overflow bug for a
          // blank-row bug, which is harder to notice and worse.
          const roomOutside = 100 - pct;
          const labelFitsInside = (pct > 34 && row.label.length <= 28) || roomOutside < 25;

          return (
            <div className="barlist-row" key={row.label}>
              <div className="barlist-track">
                <div
                  className="barlist-fill"
                  style={{ width: `${pct}%` }}
                  title={`${row.label}: ${formatValue === formatCompact ? row.value.toLocaleString('en-US') : formatValue(row.value)}`}
                />
                {labelFitsInside ? (
                  // Bounded by the BAR, not the track, so white text can never run off the
                  // coloured fill onto the card background where it would be invisible.
                  <span
                    className="barlist-label"
                    style={{ color: '#fff', maxWidth: `calc(${pct}% - 16px)` }}
                  >
                    {row.label}
                  </span>
                ) : (
                  <span
                    className="barlist-label"
                    // The max-width MUST shrink by however far the label was pushed out. The CSS
                    // rule alone allows `calc(100% - 16px)` measured from the TRACK's start, so a
                    // label beginning at 60% could claim 160% of the track — running under the
                    // count column and, because it is absolutely positioned, dragging the whole
                    // page into horizontal scroll. Long titles (the Arabic page titles are the
                    // worst case) hit this on every row.
                    style={{
                      insetInlineStart: `calc(${pct}% + 8px)`,
                      maxWidth: `calc(${100 - pct}% - 16px)`,
                    }}
                  >
                    {row.label}
                  </span>
                )}
              </div>
              <span className="barlist-value">{formatValue(row.value)}</span>
            </div>
          );
        })}
      </div>
      {rows.length > limit && (
        <p className="t-label subtle" style={{ margin: '12px 0 0' }}>
          and {rows.length - limit} more
        </p>
      )}
    </>
  );
}
